import type { Tenant } from "../tenants.ts";
import type {
  ClaimOutcome,
  ClaimPresentation,
  ClaimProviding,
  ReceivingDestination,
  RecipientSubmission,
} from "./claim-experience.ts";
import { missingRequirements, type RecipientRequirements } from "./recipient-requirements.ts";

/**
 * The synthetic claim provider.
 *
 * Deterministic fixtures and two timers. It contacts nothing, reads nothing,
 * stores nothing and mints nothing. Every value it returns is a module constant
 * or a function of the scenario it was constructed with, so the same scenario
 * produces the same screens on every run — which is what makes the product tests
 * and the screenshots evidence rather than anecdote.
 *
 * ## Why the delays exist
 *
 * A claim in production is a round trip to a bank, and an interface designed
 * against an instant fixture is an interface with no progress state. The delays
 * are here so the `processing` screen is a real screen that has to be designed,
 * not so the build can pretend a bank was involved. Tests construct the provider
 * with zero delay.
 *
 * ## What happens to the recipient details
 *
 * **Nothing.** `establishDestination` reads the submission only to confirm the
 * flow handed over what the tenant's configuration required, and then returns a
 * label. No value is stored on this object, copied into the destination,
 * returned, logged or serialised — there is no field on this class that could
 * hold one, which is a stronger statement than a promise not to use it.
 *
 * That is the synthetic stand-in for the production custody model: the sending
 * bank collects, validates and holds recipient payout information, and Nura
 * consumes only a safe label and, eventually, an opaque bank-issued reference.
 *
 * ## What it is not
 *
 * Not a claim. Not a payout. Not an eligibility check. Not a linkage to a
 * transfer. Not persistence — nothing survives the object. Not an identifier
 * mint: it issues no reference of any kind, opaque or otherwise, because
 * `docs/roadmap/PHASE_03_EARLY_FOUNDATION_DECISION.md` § Identifier boundary
 * lets client code carry a server-issued identifier and never create one, and
 * this build has no server to be issued one by.
 */

/** Which recipient is being modelled. */
export type RecipientKind = "first-time" | "returning";

/** How the synthetic claim ends. A reference dial, never a claim-security state. */
export type SyntheticOutcome = "completes" | "unavailable";

export interface ReceiverScenario {
  readonly recipient: RecipientKind;
  readonly outcome: SyntheticOutcome;
}

/** The scenario the reference entry opens with. */
export const DEFAULT_SCENARIO: ReceiverScenario = {
  recipient: "first-time",
  outcome: "completes",
};

/**
 * The one amount this build ever shows a claimant, in minor units.
 *
 * Ten thousand minor units — one hundred major units in every currency the
 * reference tenants use, since all of them carry two minor digits in the pinned
 * ISO 4217 snapshot. The amount is a `bigint`
 * from here to the screen; `CLAUDE.md` § Transaction integrity allows nothing
 * else, and a fixture is not an exception to it.
 */
const FIXTURE_AMOUNT_MINOR = 10_000n;

/**
 * The only way a destination is ever described.
 *
 * One safe label, used for a destination established a moment ago and for one
 * established long before. No digits, no masked tail, no rail identifier: a
 * returning recipient is shown that a confirmed destination exists, never what
 * it is, because Nura does not hold what it is.
 *
 * **Rail-neutral on purpose.** The destination may be an IBAN, an account
 * number, a phone number or something else a bank configures, so the label
 * cannot say "account". It also says *confirmed*, not *verified*: confirmation
 * is what the bank has actually reported back, and claiming verification would
 * assert a stronger check than anything in this architecture performs.
 */
const CONFIRMED_DESTINATION_LABEL = "Receiving details confirmed";

/** Time the synthetic bank-side confirmation appears to take. */
const SETUP_DELAY_MS = 900;

/** Time the synthetic claim appears to take. */
const CLAIM_DELAY_MS = 1_400;

export interface SyntheticClaimProviderOptions {
  readonly scenario: ReceiverScenario;
  /** The sending institution: its currency, and what it requires of a recipient. */
  readonly tenant: Tenant;
  /** Override both delays. Tests pass 0; nothing else should. */
  readonly delayMs?: number;
}

function wait(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export class SyntheticClaimProvider implements ClaimProviding {
  readonly presentation: ClaimPresentation;
  readonly requirements: RecipientRequirements;
  readonly #scenario: ReceiverScenario;
  readonly #setupDelayMs: number;
  readonly #claimDelayMs: number;

  constructor(options: SyntheticClaimProviderOptions) {
    this.#scenario = options.scenario;
    this.#setupDelayMs = options.delayMs ?? SETUP_DELAY_MS;
    this.#claimDelayMs = options.delayMs ?? CLAIM_DELAY_MS;
    // Straight from the tenant. The provider does not know which bank this is
    // and has no branch that could learn.
    this.requirements = options.tenant.recipient;
    this.presentation = {
      amountMinor: FIXTURE_AMOUNT_MINOR,
      // The amount follows the institution on screen, so a fully white-labelled
      // Nordic tenant does not present a claim in a currency it does not use.
      currency: options.tenant.currency.defaultCurrency,
    };
  }

  establishedDestination(): ReceivingDestination | undefined {
    if (this.#scenario.recipient !== "returning") return undefined;
    return { label: CONFIRMED_DESTINATION_LABEL, confirmedNow: false };
  }

  async establishDestination(submission: RecipientSubmission): Promise<ReceivingDestination> {
    const values = submission.values;
    if (values !== undefined && missingRequirements(this.requirements, values).length > 0) {
      // A real bank would reject an incomplete submission; the synthetic one
      // refuses to invent a destination for it. The message names no value.
      throw new Error("recipient details are incomplete");
    }

    await wait(this.#setupDelayMs);
    // Nothing from `values` reaches this object or this return. The label is a
    // module constant.
    return { label: CONFIRMED_DESTINATION_LABEL, confirmedNow: true };
  }

  async completeClaim(): Promise<ClaimOutcome> {
    await wait(this.#claimDelayMs);
    return this.#scenario.outcome === "completes" ? "completed" : "unavailable";
  }
}
